import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { isDevFeedbackEnabled } from './env';
import { FeedbackPanel } from './FeedbackPanel';
import { pushPendingCapturedId } from './capturedIdBuffer';
import { ackReflected, loadUnackedReflected } from './reflectedNotice';

interface Props {
  appCode: string;
  /** 런처 아이콘 (앱별 캐릭터) — 기본 🐶, navido 는 캐릭터 여우주라 🦊 (2026-10-06) */
  icon?: string;
}

type PanelState = 'closed' | 'minimized' | 'expanded';
const STATE_KEY = 'dev-feedback-panel-state';
const POS_KEY = 'dev-feedback-launcher-pos';
const CAPTURE_ONCE_EVENT = 'dev-id-inspector:capture-once';
const CAPTURED_EVENT = 'dev-id-inspector:captured';

// 런처 크기: grip 22 + 🐶 38 + `+` 32
const LAUNCHER_W = 92;
const LAUNCHER_H = 40;
// 반영됨 표시 재조회 주기 (탭 복귀 시에도 조회)
const REFLECTED_POLL_MS = 5 * 60 * 1000;

// 사용자가 이동한 위치. viewport 우/하단 원점 기준 (right, bottom · px).
interface LauncherPos { right: number; bottom: number; }
const DEFAULT_POS: LauncherPos = { right: 16, bottom: 88 };


const readPos = (): LauncherPos => {
  try {
    const raw = localStorage.getItem(POS_KEY);
    if (!raw) return DEFAULT_POS;
    const parsed = JSON.parse(raw) as LauncherPos;
    if (typeof parsed.right !== 'number' || typeof parsed.bottom !== 'number') return DEFAULT_POS;
    return parsed;
  } catch {
    return DEFAULT_POS;
  }
};

const clampToViewport = (pos: LauncherPos, w: number, h: number): LauncherPos => {
  if (typeof window === 'undefined') return pos;
  const maxRight = Math.max(0, window.innerWidth - w);
  const maxBottom = Math.max(0, window.innerHeight - h);
  return {
    right: Math.min(Math.max(0, pos.right), maxRight),
    bottom: Math.min(Math.max(0, pos.bottom), maxBottom),
  };
};

export const FeedbackLauncher: React.FC<Props> = ({ appCode, icon = '🐶' }) => {
  const [panelState, setPanelState] = useState<PanelState>(() => {
    try {
      return ((localStorage.getItem(STATE_KEY) as PanelState) || 'closed') as PanelState;
    } catch {
      return 'closed';
    }
  });
  const [pos, setPos] = useState<LauncherPos>(() => readPos());
  const [capturing, setCapturing] = useState(false);
  // 2026-10-20 lotus 기준: 내 피드백 중 개발 반영 완료 + 아직 확인 안 한 id → 런처 위 '반영됨' 표시, 누르면 사라짐
  const [reflected, setReflected] = useState<number[]>([]);
  const dragStartRef = useRef<{ x: number; y: number; startRight: number; startBottom: number; moved: boolean } | null>(null);
  // 이벤트 리스너 안에서 최신 panelState 를 보기 위한 ref (stale closure 방지)
  const panelStateRef = useRef<PanelState>(panelState);
  panelStateRef.current = panelState;

  useEffect(() => {
    try {
      localStorage.setItem(STATE_KEY, panelState);
    } catch {
      /* ignore */
    }
  }, [panelState]);

  // DevIdInspector 수집 모드 on/off 를 `+` 버튼 표시에 반영 (채팅 버튼·pill off·캡처 완료 모두 이 이벤트로 통지됨)
  useEffect(() => {
    const onToggle = (e: Event) => {
      const detail = (e as CustomEvent<{ on?: boolean }>).detail;
      if (detail && typeof detail.on === 'boolean') setCapturing(detail.on);
      else setCapturing((v) => !v);
    };
    window.addEventListener(CAPTURE_ONCE_EVENT, onToggle);
    return () => window.removeEventListener(CAPTURE_ONCE_EVENT, onToggle);
  }, []);

  // id 수집 완료 → `#id` 는 DevIdInspector 가 즉시 클립보드 복사 + "복사됨" 알림 (패널은 열지 않음, 2026-10-04 요청).
  // 패널이 닫혀/최소화 상태면 버퍼에 담아 두어, 나중에 패널을 펼치면 입력창에 자동 삽입.
  // expanded 상태면 FeedbackChat 리스너가 직접 삽입하므로 여기선 관여 안 함.
  useEffect(() => {
    const onCaptured = (e: Event) => {
      const detail = (e as CustomEvent<{ id?: string }>).detail;
      if (!detail?.id) return;
      if (panelStateRef.current === 'expanded') return;
      pushPendingCapturedId(detail.id);
    };
    window.addEventListener(CAPTURED_EVENT, onCaptured);
    return () => window.removeEventListener(CAPTURED_EVENT, onCaptured);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(POS_KEY, JSON.stringify(pos));
    } catch {
      /* ignore */
    }
  }, [pos]);

  useEffect(() => {
    if (!isDevFeedbackEnabled()) return undefined;
    let alive = true;
    const refresh = () => {
      loadUnackedReflected()
        .then((ids) => { if (alive) setReflected(ids); })
        .catch(() => { /* 표시용 — 실패 시 조용히 무시 */ });
    };
    refresh();
    const timer = window.setInterval(refresh, REFLECTED_POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  if (!isDevFeedbackEnabled()) return null;
  if (typeof document === 'undefined') return null;

  const dismissReflected = () => {
    ackReflected(reflected);
    setReflected([]);
  };

  const openPanel = () => {
    // 모바일도 새 탭 대신 inline bottom-sheet 로 열도록 통합 (2026-09-16)
    // FeedbackPanel 내부에서 isMobile 감지 → 화면 하단 절반 sheet 로 렌더.
    setPanelState((prev) => (prev === 'expanded' ? 'minimized' : 'expanded'));
  };

  // `+`: 화면 요소 id 수집 모드 토글. 요소 클릭 → `#id` 즉시 클립보드 복사 (패널 열려 있으면 입력창에도 삽입).
  const toggleCapture = () => {
    const next = !capturing;
    setCapturing(next);
    window.dispatchEvent(new CustomEvent(CAPTURE_ONCE_EVENT, { detail: { on: next } }));
  };

  // 드래그: 왼쪽 grip 영역에서만 시작. 다른 영역 클릭은 openPanel 로 소비.
  const onGripPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      startRight: pos.right,
      startBottom: pos.bottom,
      moved: false,
    };
  };
  const onGripPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const s = dragStartRef.current;
    if (!s) return;
    const dx = e.clientX - s.x;
    const dy = e.clientY - s.y;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) s.moved = true;
    const next = clampToViewport(
      { right: s.startRight - dx, bottom: s.startBottom - dy },
      LAUNCHER_W,
      LAUNCHER_H,
    );
    setPos(next);
  };
  const onGripPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const s = dragStartRef.current;
    dragStartRef.current = null;
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    // 이동 없으면 클릭으로 취급 — 드래그가 아니라 그립 탭도 openPanel
    if (s && !s.moved) openPanel();
  };

  return createPortal(
    <>
      {reflected.length > 0 && (
        <button
          id="dev-feedback-reflected-badge"
          type="button"
          onClick={dismissReflected}
          title="누르면 사라집니다"
          aria-label={`등록한 피드백 ${reflected.length}건 개발 반영됨 · 누르면 닫힘`}
          style={{
            position: 'fixed',
            bottom: `calc(${pos.bottom + LAUNCHER_H + 6}px + env(safe-area-inset-bottom, 0px))`,
            right: pos.right,
            zIndex: panelState === 'expanded' || capturing ? 2147483647 : 1200,
            padding: '3px 9px',
            borderRadius: 12,
            border: '1px solid #16a34a',
            background: '#f0fdf4',
            color: '#15803d',
            fontSize: 11,
            fontWeight: 600,
            lineHeight: 1.4,
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
            cursor: 'pointer',
          }}
        >
          ✓ 피드백 {reflected.length}건 반영됨 ×
        </button>
      )}
      <div
        id="dev-feedback-launcher"
        data-dev-feedback-launcher
        role="button"
        aria-label="개발 피드백 (local/dev 전용)"
        title={`왼쪽 손잡이로 이동 · ${icon} 열기 · + 요소 id 수집`}
        style={{
          position: 'fixed',
          bottom: `calc(${pos.bottom}px + env(safe-area-inset-bottom, 0px))`,
          right: pos.right,
          // Closed launcher must not cover product dialogs (MUI modal layer 1300).
          // 수집 모드 중엔 Inspector outline layer(2147483640) 위로 올려 `×` 로 취소 가능하게.
          zIndex: panelState === 'expanded' || capturing ? 2147483647 : 1200,
          width: LAUNCHER_W,
          height: LAUNCHER_H,
          borderRadius: 20,
          background: '#e94d20',
          color: 'white',
          border: '2px solid white',
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 0,
          overflow: 'hidden',
          touchAction: 'none',
        }}
      >
        {/* 왼쪽 grip: 드래그 손잡이 */}
        <div
          data-dev-feedback-launcher-grip
          onPointerDown={onGripPointerDown}
          onPointerMove={onGripPointerMove}
          onPointerUp={onGripPointerUp}
          onPointerCancel={onGripPointerUp}
          style={{
            width: 22,
            height: '100%',
            cursor: 'grab',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            background: 'rgba(0,0,0,0.15)',
            userSelect: 'none',
          }}
        >
          <span style={{ width: 2, height: 14, background: 'white', borderRadius: 1 }} />
          <span style={{ width: 2, height: 14, background: 'white', borderRadius: 1 }} />
        </div>
        {/* 오른쪽 아이콘: 클릭 → 패널 open */}
        <button
          type="button"
          data-dev-feedback-launcher-icon
          onClick={openPanel}
          style={{
            flex: 1,
            height: '100%',
            background: 'transparent',
            border: 'none',
            color: 'white',
            fontSize: 22,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            lineHeight: 1,
          }}
          aria-label="개발 피드백 열기"
        >
          {icon}
        </button>
        {/* 오른쪽 `+`: 클릭 → 요소 id 수집 모드. 요소 클릭 즉시 #id 클립보드 복사 */}
        <button
          type="button"
          data-dev-feedback-launcher-capture
          onClick={toggleCapture}
          title={capturing ? 'id 수집 취소' : '화면 요소 클릭하면 #id 바로 복사'}
          aria-label={capturing ? 'id 수집 취소' : '요소 id 수집'}
          aria-pressed={capturing}
          style={{
            width: 32,
            height: '100%',
            background: capturing ? 'white' : 'rgba(255,255,255,0.18)',
            border: 'none',
            borderLeft: '1px solid rgba(255,255,255,0.5)',
            color: capturing ? '#e94d20' : 'white',
            fontSize: 22,
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            lineHeight: 1,
          }}
        >
          {capturing ? '×' : '+'}
        </button>
      </div>
      {panelState !== 'closed' && (
        <FeedbackPanel
          appCode={appCode}
          state={panelState === 'expanded' ? 'expanded' : 'minimized'}
          onExpand={() => setPanelState('expanded')}
          onMinimize={() => setPanelState('minimized')}
          onClose={() => setPanelState('closed')}
        />
      )}
    </>,
    document.body,
  );
};
